import SocialFollow from '../social-follow/SocialFollow';

export type FacebookFollowProps = {
    facebookUrl?: string;
    pageName?: string;
    handle?: string;
    title?: string;
    description?: string;
    buttonLabel?: string;
    followerCount?: string;
    className?: string;
    darkMode?: boolean;
    accentColor?: string;
    openInNewTab?: boolean;
};

const FacebookMark = ({
    className,
    color = 'currentColor',
    glyphColor = '#FFFFFF',
}: {
    className?: string;
    color?: string;
    glyphColor?: string;
}) => (
    <svg
        className={className}
        viewBox="0 0 48 48"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="24" cy="24" r="22" fill={color} />
        <path
            fill={glyphColor}
            d="M30.2 15.2h-3.1c-2.4 0-2.9 1.2-2.9 2.9v3.8h5.8l-.8 5.9h-5v15h-6.1v-15H13v-5.9h5.1v-4.4c0-5 3.1-7.8 7.6-7.8 2.1 0 4 .2 4.5.2v5.3Z"
        />
    </svg>
);

const FacebookFollowGraphic = ({
    accentColor,
    darkMode,
}: {
    accentColor: string;
    darkMode: boolean;
}) => {
    const panelFill = darkMode ? '#0F172A' : '#EFF6FF';
    const softFill = darkMode ? '#1E293B' : '#DBEAFE';
    const textFill = darkMode ? '#CBD5E1' : '#64748B';
    const cardFill = darkMode ? '#111827' : '#FFFFFF';

    return (
        <svg
            className="h-full min-h-[210px] w-full"
            viewBox="0 0 360 260"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect x="16" y="18" width="328" height="224" rx="8" fill={panelFill} />
            <circle cx="112" cy="98" r="52" fill={accentColor} />
            <path
                fill="#FFFFFF"
                d="M126.6 80.3h-7.1c-4.3 0-5.1 2.1-5.1 5.1v6.7h11.9l-1.5 12.1h-10.4v34.4h-12.5v-34.4H91.5V92.1h10.4v-7.7c0-10.4 6.4-16 15.6-16 4.4 0 8.2.3 9.1.5v11.4Z"
            />

            <rect x="176" y="62" width="92" height="12" rx="6" fill={softFill} />
            <rect x="176" y="88" width="132" height="12" rx="6" fill={textFill} opacity=".65" />
            <rect x="176" y="112" width="96" height="12" rx="6" fill={textFill} opacity=".4" />

            <g transform="translate(48 164)">
                <rect width="150" height="46" rx="8" fill={cardFill} />
                <circle cx="25" cy="23" r="12" fill={accentColor} opacity=".92" />
                <path
                    d="M20 23.5h5.2v-5.2h5.1v5.2h5.2v5.1h-5.2v5.2h-5.1v-5.2H20v-5.1Z"
                    fill="#FFFFFF"
                />
                <rect x="50" y="15" width="70" height="7" rx="3.5" fill={textFill} opacity=".65" />
                <rect x="50" y="28" width="48" height="7" rx="3.5" fill={textFill} opacity=".35" />
            </g>

            <g transform="translate(212 142)">
                <rect width="92" height="70" rx="8" fill={cardFill} />
                <circle cx="25" cy="26" r="13" fill={accentColor} opacity=".16" />
                <path
                    d="M25.5 15.5c-4.1 0-7.4 3-7.4 6.7 0 2.1 1.1 4 2.9 5.2l-.8 4.3 4-2.3h1.3c4.1 0 7.4-3 7.4-6.7s-3.3-7.2-7.4-7.2Z"
                    fill={accentColor}
                />
                <rect x="48" y="19" width="28" height="7" rx="3.5" fill={textFill} opacity=".55" />
                <rect x="48" y="34" width="22" height="7" rx="3.5" fill={textFill} opacity=".35" />
                <rect x="16" y="53" width="60" height="6" rx="3" fill={softFill} />
            </g>

            <circle cx="308" cy="56" r="22" fill={accentColor} />
            <path
                d="M301.2 55.4h4.1V51c0-3.9 2.5-6.3 6.1-6.3 1.8 0 3.3.2 3.7.2v4.4h-2.6c-1.9 0-2.3.9-2.3 2.2v3.9h4.6l-.7 4.7h-3.9v11.5h-4.9V60.1h-4.1v-4.7Z"
                fill="#FFFFFF"
            />
            <circle cx="308" cy="56" r="28" fill={accentColor} opacity=".14" />
        </svg>
    );
};

const FacebookFollow = ({
    facebookUrl = 'https://www.facebook.com/',
    pageName = 'Our Facebook page',
    handle,
    title = 'Follow us on Facebook',
    description = 'Keep up with new posts, updates, behind-the-scenes moments, and the latest news from our team.',
    buttonLabel = 'Follow on Facebook',
    followerCount,
    className = '',
    darkMode = false,
    accentColor = '#1877F2',
    openInNewTab = true,
}: FacebookFollowProps) => {
    return (
        <SocialFollow
            platformName="Facebook"
            profileUrl={facebookUrl}
            profileName={pageName}
            handle={handle}
            title={title}
            description={description}
            buttonLabel={buttonLabel}
            followerCount={followerCount}
            className={className}
            darkMode={darkMode}
            accentColor={accentColor}
            openInNewTab={openInNewTab}
            logo={<FacebookMark className="h-11 w-11 flex-shrink-0" color={accentColor} />}
            buttonLogo={
                <FacebookMark
                    className="h-5 w-5 flex-shrink-0"
                    color="#FFFFFF"
                    glyphColor={accentColor}
                />
            }
            graphic={<FacebookFollowGraphic accentColor={accentColor} darkMode={darkMode} />}
        />
    );
};

export default FacebookFollow;
